import {useEffect, useState} from 'react';
import {Button, Typography} from '@mui/material';
import {ObjectType, runIntegrationAction} from '../../../api/integrations';
import {IntegrationOverlayCommonProps} from '../../Media/Asset/View/AssetView.tsx';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import IntegrationPanelContent from '../Common/IntegrationPanelContent';
import SaveAsButton from '../../Media/Asset/Actions/SaveAsButton';
import {useChannelRegistration} from '../../../lib/pusher.ts';
import {useIntegrationData} from '../useIntegrationData.ts';
import {AssetIntegrationActionsProps, Integration} from '../types.ts';
import {useTranslation} from 'react-i18next';
import {
    ReactCompareSlider,
    ReactCompareSliderImage,
} from 'react-compare-slider';

function RemoveBgComparison({
    left,
    right,
    dimensions,
}: {
    left: string;
    right: string;
} & IntegrationOverlayCommonProps) {
    return (
        <div
            style={{
                width: dimensions.width,
                maxWidth: dimensions.width,
                maxHeight: dimensions.height,
            }}
        >
            <ReactCompareSlider
                boundsPadding={0}
                position={50}
                itemOne={<ReactCompareSliderImage src={left} alt="Original" />}
                itemTwo={
                    <ReactCompareSliderImage
                        src={right}
                        alt="BG Removed"
                        style={{
                            backgroundColor: 'white',
                            backgroundImage:
                                'linear-gradient(45deg, #ccc 25%, transparent 25%),linear-gradient(-45deg, #ccc 25%, transparent 25%),linear-gradient(45deg, transparent 75%, #ccc 75%),linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                            backgroundPosition:
                                '0 0, 0 10px, 10px -10px, -10px 0px',
                            backgroundSize: '20px 20px',
                            width: '100%',
                        }}
                    />
                }
            />
        </div>
    );
}

type Props = {} & AssetIntegrationActionsProps;

export default function RemoveBGAssetEditorActions({
    asset,
    file,
    integration,
    setIntegrationOverlay,
    enableInc,
}: Props) {
    const {t} = useTranslation();
    const [running, setRunning] = useState(false);
    const {data, load: loadData} = useIntegrationData({
        objectType: ObjectType.File,
        objectId: file.id,
        integrationId: integration.id,
        defaultData: integration.data,
    });

    const bgRemovedFile = data.pages.flat().find(d => d.name === 'file')?.value;

    const process = async () => {
        setRunning(true);
        try {
            await runIntegrationAction('process', integration.id, {
                fileId: file.id,
            });
        } catch (e) {
            setRunning(false);
            throw e;
        }
    };

    useChannelRegistration(
        `file-${file.id}`,
        `integration:${Integration.RemoveBg}`,
        () => {
            setRunning(false);
            loadData();
        }
    );

    useEffect(() => {
        if (enableInc && bgRemovedFile) {
            setIntegrationOverlay(
                RemoveBgComparison,
                {
                    left: file.url,
                    right: bgRemovedFile.url,
                },
                true
            );
        }
    }, [enableInc, bgRemovedFile]);

    if (bgRemovedFile) {
        return (
            <IntegrationPanelContent>
                <Typography sx={{mb: 3}}>
                    {t(
                        'remove_bg.use_slider_to_compare',
                        `Use slider to compare`
                    )}
                </Typography>

                <SaveAsButton
                    asset={asset}
                    file={bgRemovedFile}
                    suggestedName={t('remove_bg.bg_removed', {
                        defaultValue: `{{name}} - BG removed`,
                        name: asset.name,
                    })}
                />
            </IntegrationPanelContent>
        );
    }

    return (
        <IntegrationPanelContent>
            {integration.capabilities.interact ? (
                <Button
                    startIcon={<AutoFixHighIcon />}
                    onClick={process}
                    disabled={running}
                    variant={'contained'}
                >
                    {t('remove_bg.remove_bg', `Remove BG`)}
                </Button>
            ) : (
                <Typography>
                    {t(
                        'remove_bg.cannot_interact',
                        `You don't have sufficient permissions to remove BG`
                    )}
                </Typography>
            )}
        </IntegrationPanelContent>
    );
}
